import { FunnelSimpleIcon, LightbulbIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import {
  limitingRuleKey,
  relaxationText,
  useExplanationContext,
  type EmptyReason,
  type LimitingCriterion,
  type RelaxationSuggestionData,
} from '@entities/search-run';
import { Button } from '@shared/ui';

import styles from './EmptyResults.module.css';

interface EmptyResultsProps {
  reason: EmptyReason;
  limitingCriteria: readonly LimitingCriterion[];
  suggestions: readonly RelaxationSuggestionData[];
  /** Сколько автомобилей прошло обязательные критерии; `null` — прогон не сообщил. */
  eligibleCount: number | null;
  /** Сброс дополнительных фильтров. Критерии чата при этом не трогаются. */
  onResetFilters: () => void;
}

/**
 * Пустой результат (FE-081, AC-09, MVP_PRD §19).
 *
 * Два разных пустых состояния, которые нельзя объединять:
 *
 * * **ни один автомобиль не прошёл обязательные критерии.** Показывается, какие
 *   условия сколько отсеяли, и до трёх предложений с конкретной величиной и
 *   эффектом. Числа считает backend по воронке исключений, а не «на глаз»;
 * * **не прошли дополнительные фильтры.** Тогда сбрасываются только фильтры —
 *   критерии диалога остаются нетронутыми;
 * * **критерии прошли, но порог качества рабочего набора — никто**
 *   (MVP_PRD §34.1). Кнопки сброса здесь нет: фильтров никто не ставил, и
 *   кнопка, которая ничего не меняет, хуже её отсутствия.
 *
 * **Предложение не применяется нажатием.** Кнопки «применить» здесь нет и быть
 * не может: изменить критерии может только пользователь в чате, и это создаёт
 * новую версию критериев и новый прогон (MATCHING §5.3).
 */
export function EmptyResults({
  reason,
  limitingCriteria,
  suggestions,
  eligibleCount,
  onResetFilters,
}: EmptyResultsProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const context = useExplanationContext();

  if (reason === 'NO_REGION_MATCHES') {
    // Автомобили нашлись, но дальше согласованной ступени географии. Кнопки
    // здесь нет намеренно: расширение — это ответ Karl'у в рельсе, и второй
    // способ согласиться означал бы два разных пути к одному решению.
    return (
      <section className={styles.block} aria-labelledby={titleId}>
        <h2 id={titleId} className={styles.title}>
          {t('results.empty.region.title')}
        </h2>
        <p className={styles.text}>{t('results.empty.region.text')}</p>
      </section>
    );
  }

  if (reason === 'ONLY_WEAK_MATCHES') {
    return (
      <section className={styles.block} aria-labelledby={titleId}>
        <h2 id={titleId} className={styles.title}>
          {t('results.empty.weak.title')}
        </h2>
        {eligibleCount !== null ? (
          <p className={styles.text}>{t('results.empty.weak.text', { count: eligibleCount })}</p>
        ) : null}
        <p className={styles.note}>{t('results.empty.editInRail')}</p>
      </section>
    );
  }

  if (reason === 'NO_FILTER_MATCHES') {
    return (
      <section className={styles.block} aria-labelledby={titleId}>
        <h2 id={titleId} className={styles.title}>
          {t('results.empty.filtered.title')}
        </h2>
        <p className={styles.text}>{t('results.empty.filtered.text')}</p>
        <Button
          variant="secondary"
          onClick={onResetFilters}
          iconEnd={<FunnelSimpleIcon size={16} />}
        >
          {t('results.empty.filtered.reset')}
        </Button>
      </section>
    );
  }

  const visibleSuggestions = suggestions.flatMap((suggestion) => {
    const text = relaxationText(suggestion, context);
    return text === null ? [] : [{ suggestion, text }];
  });

  return (
    <section className={styles.block} aria-labelledby={titleId}>
      <h2 id={titleId} className={styles.title}>
        {t('results.empty.none.title')}
      </h2>
      <p className={styles.text}>{t('results.empty.none.text')}</p>

      {limitingCriteria.length > 0 ? (
        <div className={styles.section}>
          <h3 className={styles.subtitle}>{t('results.empty.limiting')}</h3>
          <ul className={styles.limiting}>
            {limitingCriteria.map((item) =>
              context.exists(limitingRuleKey(item.rule)) ? (
                <li key={item.rule}>
                  <span>{t(limitingRuleKey(item.rule))}</span>
                  <span className={`${styles.count} tabular`}>
                    {t('results.empty.excluded', { count: item.excluded })}
                  </span>
                </li>
              ) : null,
            )}
          </ul>
        </div>
      ) : null}

      {visibleSuggestions.length > 0 ? (
        <div className={styles.section}>
          <h3 className={styles.subtitle}>
            <LightbulbIcon size={18} weight="fill" aria-hidden="true" />
            {t('results.empty.suggestions')}
          </h3>
          <ul className={styles.suggestions}>
            {visibleSuggestions.map(({ suggestion, text }) => (
              <li key={suggestion.code}>
                <span>{text}</span>
                <span className={`${styles.effect} tabular`}>
                  {t('results.empty.wouldAdd', { count: suggestion.would_add })}
                </span>
              </li>
            ))}
          </ul>
          <p className={styles.note}>{t('results.empty.manualOnly')}</p>
        </div>
      ) : null}

      {/* Кнопки «вернуться к диалогу» здесь нет: диалог с Karl стоит рядом, в
          рельсе, и уточнить запрос можно не уходя с экрана (FE-193). */}
      <p className={styles.note}>{t('results.empty.editInRail')}</p>
    </section>
  );
}
